import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid } from 'components/ui';
import mapHistoryTabPresentation from 'components/Content/BeautyInsider/BeautyInsiderHistory/mapHistoryTabPresentation';
import HistoryYearAtGlanceSection from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistoryYearAtGlanceSection';
import HistoryRewardsRedeemed from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistoryRewardsRedeemed';
import HistoryPointsSection from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistoryPointsSection';
import HistoryProgramLinksSection from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistoryProgramLinksSection';
import Empty from 'constants/empty';

/**
 * Renders the BI History tab sections.
 *
 * @param {Object} props
 * @param {Object} props.profile Recognized/sign-in/BI state and user attributes for History state selection.
 * @param {string} props.region Region code used by annual activity mapping.
 * @param {Object} props.yearAtGlance Year at a Glance DTO with loading/error flags and current-year data.
 * @param {Object} props.rewardsRedeemed Rewards Redeemed DTO with loading/error flags and purchase-history data.
 * @param {Object} props.groupedPoints Points History DTO with loading/error flags and grouped point records.
 * @param {Object} props.actions Retry and shop-now callbacks for section interactions.
 * @returns {React.ReactElement} History tab content.
 */
const HistoryTab = ({
    profile = Empty.Object,
    region = 'US',
    yearAtGlance = Empty.Object,
    rewardsRedeemed = Empty.Object,
    groupedPoints = Empty.Object,
    actions = Empty.Object
}) => {
    const presentation = mapHistoryTabPresentation({
        profile,
        region,
        yearAtGlance,
        rewardsRedeemed,
        groupedPoints,
        actions
    });
    const { sections } = presentation;

    return (
        <Box data-testid='history-tab'>
            <Grid
                columns={['1fr', 'repeat(2, minmax(0, 1fr))']}
                marginTop={[4, 5]}
                rowGap={[4, 5]}
                columnGap={[0, 5]}
                alignItems='start'
            >
                <HistoryYearAtGlanceSection section={sections.yearAtGlance} />
                <HistoryRewardsRedeemed section={sections.rewardsRedeemed} />
                <HistoryPointsSection section={sections.pointsHistory} />
            </Grid>
            <HistoryProgramLinksSection section={sections.programLinks} />
        </Box>
    );
};

HistoryTab.propTypes = {
    profile: PropTypes.object,
    region: PropTypes.string,
    yearAtGlance: PropTypes.object,
    rewardsRedeemed: PropTypes.object,
    groupedPoints: PropTypes.object,
    actions: PropTypes.object
};

export default wrapFunctionalComponent(HistoryTab, 'HistoryTab');
